import React, { useEffect, useState } from "react";
import Select from "@/components/Select";
import api from "@/lib/api";

// Definițiile câmpurilor personalizate ale unei entități ("person" | "employment"), încărcate din server.
export function useCustomDefs(entity, enabled = true) {
  const [defs, setDefs] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    api.get("/custom-fields", { params: { entity } }).then(r => setDefs(r.data)).catch(() => setDefs([]));
  }, [entity, enabled]);
  return defs;
}

const fmtDate = (v) => { if (!v) return ""; const [y, m, d] = String(v).slice(0, 10).split("-"); return `${d}.${m}.${y}`; };
export const showValue = (d, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (d.field_type === "bool") return v ? "Da" : "Nu";
  if (d.field_type === "multiselect") return v.join(", ");
  if (d.field_type === "date") return fmtDate(v);
  return `${v}${d.unit && ["integer", "decimal"].includes(d.field_type) ? " " + d.unit : ""}`;
};

function Input({ d, value, onChange, id }) {
  const t = d.field_type;
  if (t === "textarea") return <textarea className="input" rows={3} value={value ?? ""} onChange={e => onChange(e.target.value)} data-testid={id} />;
  if (t === "integer") return <input className="input" type="number" step="1" value={value ?? ""} onChange={e => onChange(e.target.value === "" ? null : parseInt(e.target.value, 10))} data-testid={id} />;
  if (t === "decimal") return <input className="input" type="number" step="any" value={value ?? ""} onChange={e => onChange(e.target.value === "" ? null : parseFloat(e.target.value))} data-testid={id} />;
  if (t === "date") return <input className="input" type="date" value={value || ""} onChange={e => onChange(e.target.value || null)} data-testid={id} />;
  if (t === "bool") {
    return (
      <Select className="select" value={value === true ? "da" : value === false ? "nu" : ""} onChange={e => onChange(e.target.value === "" ? null : e.target.value === "da")} data-testid={id}>
        <option value="">—</option><option value="da">Da</option><option value="nu">Nu</option>
      </Select>);
  }
  if (t === "select") {
    return (
      <Select className="select" value={value ?? ""} onChange={e => onChange(e.target.value || null)} data-testid={id}>
        <option value="">—</option>{d.options.map(o => <option key={o} value={o}>{o}</option>)}
      </Select>);
  }
  if (t === "multiselect") {
    const cur = value || [];
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px" }} data-testid={id}>
        {d.options.map(o => (
          <label key={o} style={{ fontSize: 13, display: "flex", gap: 5, alignItems: "center" }}>
            <input type="checkbox" checked={cur.includes(o)} data-testid={`${id}-opt-${o}`}
              onChange={() => onChange(cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o])} />{o}
          </label>))}
      </div>);
  }
  return <input className="input" value={value ?? ""} onChange={e => onChange(e.target.value)} data-testid={id} maxLength={255} />;
}

/**
 * Câmpurile personalizate definite de client.
 * - entity="person": `values` = { cheie: { value, employment_ids, masked } }; sub fiecare câmp completat se alege spre
 *   ce rapoarte de muncă ale angajatului se trimite valoarea (`employments` = [{id, label}]).
 * - entity="employment": `values` = { cheie: valoare }; `inherited` = valorile primite de la angajat (doar citire).
 */
export default function CustomFields({ entity, defs, values, onChange, employments = [], inherited = [], prefix = "cf" }) {
  const active = defs.filter(d => d.active);
  if (active.length === 0 && inherited.length === 0) {
    return <div style={{ gridColumn: "1/-1", fontSize: 12.5, color: "#64748B" }} data-testid={`${prefix}-empty`}>
      Nu există câmpuri personalizate. Administratorul le poate defini din Parametrii › Câmpuri personalizate.</div>;
  }
  const sections = [...new Set(active.map(d => d.section || ""))];
  const get = (d) => (entity === "person" ? values?.[d.key]?.value : values?.[d.key]);
  const set = (d, v) => onChange(d.key, entity === "person" ? { ...(values?.[d.key] || {}), value: v, masked: false } : v);
  const toggleEmp = (d, id) => {
    const cur = values?.[d.key]?.employment_ids || [];
    onChange(d.key, { ...(values?.[d.key] || {}), employment_ids: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] });
  };

  return (
    <>
      {sections.map(sec => (
        <React.Fragment key={sec || "_"}>
          {sec && <div style={{ gridColumn: "1/-1", fontSize: 12, fontWeight: 700, color: "#334155", marginTop: 6 }}>{sec}</div>}
          {active.filter(d => (d.section || "") === sec).map(d => {
            const id = `${prefix}-${d.key}`;
            const masked = entity === "person" ? values?.[d.key]?.masked : false;
            const v = get(d);
            return (
              <div key={d.id} style={["textarea", "multiselect"].includes(d.field_type) ? { gridColumn: "1/-1" } : undefined}>
                <label className={"label" + (d.required ? " label-req" : "")}>
                  {d.label}{d.unit && !["integer", "decimal"].includes(d.field_type) ? ` (${d.unit})` : ""}{d.sensitive ? " 🔒" : ""}</label>
                {masked
                  ? <div className="input" style={{ color: "#94A3B8" }} data-testid={`${id}-masked`}>•••••• (necesită dreptul pentru date sensibile)</div>
                  : <Input d={d} value={v} onChange={x => set(d, x)} id={id} />}
                {entity === "person" && !masked && v !== null && v !== undefined && v !== "" && employments.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 11.5, color: "#475569" }} data-testid={`${id}-targets`}>
                    <span>Trimite spre rapoartele de muncă: </span>
                    {employments.map(e => (
                      <label key={e.id} style={{ marginRight: 10, display: "inline-flex", gap: 4, alignItems: "center" }}>
                        <input type="checkbox" checked={(values?.[d.key]?.employment_ids || []).includes(e.id)}
                          onChange={() => toggleEmp(d, e.id)} data-testid={`${id}-emp-${e.id}`} />{e.label}
                      </label>))}
                  </div>)}
              </div>);
          })}
        </React.Fragment>
      ))}
      {inherited.length > 0 && (
        <div style={{ gridColumn: "1/-1", marginTop: 8 }} data-testid={`${prefix}-inherited`}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Primite de la angajat (doar citire)</div>
          {inherited.map(x => (
            <div key={x.key} style={{ fontSize: 13, padding: "3px 0" }} data-testid={`${prefix}-inh-${x.key}`}>
              <span style={{ color: "#64748B" }}>{x.label}: </span>
              <b>{x.masked ? "••••••" : showValue(x, x.value)}</b>
            </div>))}
        </div>)}
    </>
  );
}
